import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authService } from '../api/services';

const AuthContext = createContext(null);

// Token storage keys
const TOKEN_KEY = 'auth_token';
const USER_KEY = 'auth_user';
const TOKEN_EXPIRY_KEY = 'auth_token_expiry';

// Token expiry buffer (5 minutes before actual expiry)
const EXPIRY_BUFFER = 5 * 60 * 1000;

/**
 * Safely parse JSON from localStorage
 */
const safeJsonParse = (str) => {
  try {
    return JSON.parse(str);
  } catch {
    return null;
  }
};

/**
 * Check if token is expired
 */
const isTokenExpired = () => {
  const expiry = localStorage.getItem(TOKEN_EXPIRY_KEY);
  if (!expiry) return true;
  return Date.now() > parseInt(expiry, 10) - EXPIRY_BUFFER;
};

/**
 * Clear all auth data from storage
 */
const clearAuthStorage = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  localStorage.removeItem(TOKEN_EXPIRY_KEY);
};

/**
 * Set auth data in storage
 */
const setAuthStorage = (token, user, expiresInMinutes = 1440) => {
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(USER_KEY, JSON.stringify(user));
  // Calculate expiry time
  const expiryTime = Date.now() + (expiresInMinutes * 60 * 1000);
  localStorage.setItem(TOKEN_EXPIRY_KEY, expiryTime.toString());
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Verify and restore session on mount
  useEffect(() => {
    const restoreSession = async () => {
      const token = localStorage.getItem(TOKEN_KEY);
      const savedUser = localStorage.getItem(USER_KEY);

      if (token && savedUser && !isTokenExpired()) {
        const parsedUser = safeJsonParse(savedUser);
        if (parsedUser) {
          setUser(parsedUser);
          setIsAuthenticated(true);

          // Optionally verify token with backend
          try {
            const response = await authService.getMe();
            setUser(response.data);
            localStorage.setItem(USER_KEY, JSON.stringify(response.data));
          } catch (error) {
            // Token invalid, clear storage
            if (error.response?.status === 401) {
              clearAuthStorage();
              setUser(null);
              setIsAuthenticated(false);
            }
          }
        }
      } else {
        // Token expired or missing
        clearAuthStorage();
      }

      setLoading(false);
    };

    restoreSession();
  }, []);

  // Set up token expiry check interval
  useEffect(() => {
    const checkExpiry = () => {
      if (isAuthenticated && isTokenExpired()) {
        logout();
      }
    };

    const interval = setInterval(checkExpiry, 60000); // Check every minute
    return () => clearInterval(interval);
  }, [isAuthenticated]);

  const login = useCallback(async (email, password) => {
    const response = await authService.login({ email, password });
    const { access_token, user: userData } = response.data;

    // Store auth data with expiry
    setAuthStorage(access_token, userData);
    setUser(userData);
    setIsAuthenticated(true);

    return userData;
  }, []);

  const logout = useCallback(() => {
    clearAuthStorage();
    setUser(null);
    setIsAuthenticated(false);
    // Redirect to login page
    window.location.href = '/login';
  }, []);

  const updateUser = useCallback((updates) => {
    setUser((prev) => {
      const updated = { ...prev, ...updates };
      localStorage.setItem(USER_KEY, JSON.stringify(updated));
      return updated;
    });
  }, []);

  // Get current token
  const getToken = useCallback(() => {
    if (isTokenExpired()) {
      logout();
      return null;
    }
    return localStorage.getItem(TOKEN_KEY);
  }, [logout]);

  return (
    <AuthContext.Provider
      value={{
        user,
        login,
        logout,
        loading,
        isAuthenticated,
        updateUser,
        getToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
